import { useTranslation } from "react-i18next";
import { Dropdown } from "@carbon/react";
import { DeliveryState } from "../../types";

const ALL_STATES = "ALL";

/** Every `DeliveryState`, in list order, each with its `deliveries.states.*` label key. */
const STATE_OPTIONS: { state: DeliveryState; labelKey: string }[] = [
  { state: DeliveryState.DRAFT, labelKey: "deliveries.states.draft" },
  { state: DeliveryState.SCHEDULED, labelKey: "deliveries.states.scheduled" },
  { state: DeliveryState.REJECTED, labelKey: "deliveries.states.rejected" },
  { state: DeliveryState.ACCEPTED, labelKey: "deliveries.states.accepted" },
  { state: DeliveryState.IN_PROGRESS, labelKey: "deliveries.states.inProgress" },
  { state: DeliveryState.CANCELLED, labelKey: "deliveries.states.cancelled" },
  { state: DeliveryState.DELIVERED, labelKey: "deliveries.states.delivered" },
  { state: DeliveryState.COMPLETED, labelKey: "deliveries.states.completed" },
];

interface DeliveryStateFilterProps {
  /** undefined = the "All" option. */
  value: DeliveryState | undefined;
  onChange: (state: DeliveryState | undefined) => void;
}

/**
 * The deliveries list state filter: a single labeled Carbon `Dropdown` (All + all 8 states) meant
 * to sit in a filter bar ABOVE the `DataTable`, matching the Orders/Materials list pattern
 * (`DataTable` itself has no filter slot). Shared by the buyer (`Deliveries`) and seller
 * (`SupplierDeliveries`) lists so the option set and styling stay in one place.
 */
const DeliveryStateFilter = ({ value, onChange }: DeliveryStateFilterProps) => {
  const { t } = useTranslation();

  const statusItems = [
    { id: ALL_STATES, label: t("deliveries.filter.all") },
    ...STATE_OPTIONS.map(({ state, labelKey }) => ({
      id: state,
      label: t(labelKey),
    })),
  ];
  const selectedStatusItem =
    statusItems.find((item) => item.id === (value ?? ALL_STATES)) ??
    statusItems[0];

  return (
    <Dropdown
      id="deliveries-state-filter"
      className="deliveries__state-filter"
      titleText={t("deliveries.filter.label")}
      label={t("deliveries.filter.all")}
      items={statusItems}
      selectedItem={selectedStatusItem}
      itemToString={(item) => item?.label ?? ""}
      onChange={({ selectedItem }) =>
        onChange(
          !selectedItem || selectedItem.id === ALL_STATES
            ? undefined
            : (selectedItem.id as DeliveryState)
        )
      }
    />
  );
};

export default DeliveryStateFilter;
